/**
 * CancelJobButton — user-initiated cancel for a non-terminal job (E04.6 / F05
 * FR6). Hidden once the job is terminal. A two-step inline confirm guards the
 * irreversible action; on a 200 receipt it surfaces the backend message and
 * calls onCancelled (the detail view refetches). A 409/4xx renders inline as
 * the thrown (readable) detail message. Red = danger semantics throughout.
 */

import { useState } from "react";
import { isTerminal } from "../engine/jobResults";
import * as api from "../engine/api";

export default function CancelJobButton({ jobId, status, onCancelled }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState("");

  if (isTerminal(status)) return null;

  async function doCancel() {
    setBusy(true);
    setError("");
    try {
      const res = await api.cancelJob(jobId);
      setConfirming(false);
      setReceipt(res?.message || "Cancellation requested.");
      if (typeof onCancelled === "function") onCancelled();
    } catch (err) {
      setError(err.message || "Cancel failed");
    } finally {
      setBusy(false);
    }
  }

  if (receipt) {
    return <div className="text-sm text-fg2">{receipt}</div>;
  }

  if (!confirming) {
    return (
      <div className="space-y-1">
        <button
          onClick={() => setConfirming(true)}
          className="text-sm text-danger hover:text-danger cursor-pointer bg-transparent border border-danger/40 rounded-none px-3 py-1"
        >
          Cancel job
        </button>
        {error && <div className="text-sm text-danger bg-danger-tint rounded-none px-3 py-2">{error}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-sm text-fg">Stop this job? It cannot be resumed.</div>
      <div className="flex items-center gap-2">
        <button
          onClick={doCancel}
          disabled={busy}
          className="text-sm text-danger cursor-pointer bg-danger-tint rounded-none px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? "Cancelling…" : "Yes, cancel"}
        </button>
        <button
          onClick={() => setConfirming(false)}
          disabled={busy}
          className="text-sm text-fg2 hover:text-fg cursor-pointer bg-transparent border border-line rounded-none px-3 py-1 disabled:opacity-50"
        >
          Keep running
        </button>
      </div>
      {error && <div className="text-sm text-danger bg-danger-tint rounded-none px-3 py-2">{error}</div>}
    </div>
  );
}
